import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, clearToken, API_BASE_URL, DPP_BASE_URL } from '../dppApi';
import '../App.css';

const TRANSFER_TYPES = [
    { value: 'initial_sale', label: 'Initial Sale' },
    { value: 'resale', label: 'Resale' },
    { value: 'gift', label: 'Gift' },
    { value: 'inheritance', label: 'Inheritance' },
];

const BrandDashboard = () => {
    const navigate = useNavigate();
    const token = getToken('brand');
    const [products, setProducts] = useState([]);
    const [brand, setBrand] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [error, setError] = useState(null);
    const [transferPanel, setTransferPanel] = useState(null);
    const [certPanel, setCertPanel] = useState(null);
    const [editTitle, setEditTitle] = useState(null); // null = not editing, string = editing
    const [savingTitle, setSavingTitle] = useState(false);

    const loadProducts = useCallback(async () => {
        try {
            setProducts(await apiFetch('/api/products', { token }));
        } catch (err) {
            setError(err.message);
        }
    }, [token]);

    useEffect(() => {
        if (!token) { navigate('/brand'); return; }
        loadProducts();
        apiFetch('/api/brand/me', { token }).then(setBrand).catch(() => {});
    }, [token, navigate, loadProducts]);

    const uploadLogo = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            const form = new FormData();
            form.append('logo', file);
            const data = await apiFetch('/api/brand/logo', { method: 'POST', token, body: form, isForm: true });
            setBrand(b => ({ ...b, logo_url: data.logo_url }));
        } catch (err) {
            setError(err.message);
        } finally {
            setLogoUploading(false);
        }
    };

    const saveTitle = async () => {
        setSavingTitle(true);
        try {
            await apiFetch('/api/brand/me', { method: 'PATCH', token, body: { brand_title: editTitle } });
            setBrand(b => ({ ...b, brand_title: editTitle.trim() || null }));
            setEditTitle(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingTitle(false);
        }
    };

    const logout = () => { clearToken('brand'); navigate('/brand'); };

    const openCert = (productId, docType = 'certificate') =>
        setCertPanel({ productId, docType, file: null, issuedBy: '', issuedDate: '', busy: false, error: null, done: false });

    const closeCert = () => setCertPanel(null);

    const uploadDocument = async () => {
        if (!certPanel.file) return;
        setCertPanel(p => ({ ...p, busy: true, error: null }));
        try {
            const form = new FormData();
            form.append('document', certPanel.file);
            form.append('doc_type', certPanel.docType);
            if (certPanel.issuedBy) form.append('issued_by', certPanel.issuedBy);
            if (certPanel.issuedDate) form.append('issued_date', certPanel.issuedDate);
            await apiFetch(`/api/products/${certPanel.productId}/documents`, { method: 'POST', token, body: form, isForm: true });
            setCertPanel(p => ({ ...p, busy: false, done: true }));
            loadProducts();
        } catch (err) {
            setCertPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    const openTransfer = (productId) =>
        setTransferPanel({ productId, type: 'initial_sale', link: null, busy: false, error: null });

    const closeTransfer = () => setTransferPanel(null);

    const generateLink = async () => {
        setTransferPanel(p => ({ ...p, busy: true, error: null }));
        try {
            const data = await apiFetch(`/api/products/${transferPanel.productId}/sale-token`, {
                method: 'POST',
                token,
                body: { transfer_type: transferPanel.type },
            });
            const claimUrl = `${window.location.origin}/transfer/claim/${data.token_id}`;
            setTransferPanel(p => ({ ...p, busy: false, link: claimUrl }));
        } catch (err) {
            setTransferPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    const unsold = products.filter(p => p.ownership_count === 0);
    const sold = products.filter(p => p.ownership_count > 0);

    const renderProductRow = (p, showTransfer) => (
        <div key={p.product_id}>
            <div className="dpp-product-row">
                {p.image_url && p.image_url.startsWith('http') && (
                    <img className="dpp-thumb" src={p.image_url} alt={p.name} />
                )}
                <img className="dpp-qr" src={`${API_BASE_URL}${p.qr_image_url}`} alt="QR code" />
                <div style={{ flex: 1 }}>
                    <strong>{p.name}</strong>
                    <div className="dpp-muted">{p.metal_type} {p.metal_purity}</div>
                    <a href={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`} target="_blank" rel="noreferrer">Open passport</a>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                    <button
                        className="dpp-link-button"
                        onClick={() =>
                            certPanel?.productId === p.product_id && certPanel?.docType === 'certificate'
                                ? closeCert()
                                : openCert(p.product_id, 'certificate')
                        }
                    >
                        {certPanel?.productId === p.product_id && certPanel?.docType === 'certificate' ? 'Cancel' : 'Attach certificate'}
                    </button>
                    {!showTransfer && (
                        <button
                            className="dpp-link-button"
                            onClick={() =>
                                certPanel?.productId === p.product_id && certPanel?.docType === 'receipt'
                                    ? closeCert()
                                    : openCert(p.product_id, 'receipt')
                            }
                        >
                            {certPanel?.productId === p.product_id && certPanel?.docType === 'receipt' ? 'Cancel' : 'Upload receipt'}
                        </button>
                    )}
                    {showTransfer && (
                        <button
                            className="dpp-link-button"
                            onClick={() =>
                                transferPanel?.productId === p.product_id
                                    ? closeTransfer()
                                    : openTransfer(p.product_id)
                            }
                        >
                            {transferPanel?.productId === p.product_id ? 'Cancel' : 'Transfer'}
                        </button>
                    )}
                </div>
            </div>

            {certPanel?.productId === p.product_id && (
                <div className="dpp-transfer-panel">
                    {certPanel.done ? (
                        <p className="dpp-transfer-label" style={{ color: 'green' }}>
                            {certPanel.docType === 'receipt' ? 'Receipt uploaded.' : 'Certificate uploaded.'}
                        </p>
                    ) : (
                        <>
                            <label className="dpp-transfer-label">
                                {certPanel.docType === 'receipt' ? 'Receipt file' : 'Certificate file'}
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={e => setCertPanel(p => ({ ...p, file: e.target.files[0] || null }))}
                            />
                            <label className="dpp-transfer-label">Issued by (optional)</label>
                            <input
                                className="dpp-input"
                                placeholder={certPanel.docType === 'receipt' ? 'e.g. Store name' : 'e.g. GIA'}
                                value={certPanel.issuedBy}
                                onChange={e => setCertPanel(p => ({ ...p, issuedBy: e.target.value }))}
                            />
                            <label className="dpp-transfer-label">Issued date (optional)</label>
                            <input
                                className="dpp-input"
                                type="date"
                                value={certPanel.issuedDate}
                                onChange={e => setCertPanel(p => ({ ...p, issuedDate: e.target.value }))}
                            />
                            {certPanel.error && <p className="dpp-error">{certPanel.error}</p>}
                            <button
                                className="dpp-button"
                                onClick={uploadDocument}
                                disabled={certPanel.busy || !certPanel.file}
                            >
                                {certPanel.busy ? 'Uploading…' : 'Upload'}
                            </button>
                        </>
                    )}
                </div>
            )}

            {showTransfer && transferPanel?.productId === p.product_id && (
                <div className="dpp-transfer-panel">
                    {!transferPanel.link ? (
                        <>
                            <label className="dpp-transfer-label">Transfer type</label>
                            <select
                                className="dpp-input"
                                value={transferPanel.type}
                                onChange={e => setTransferPanel(prev => ({ ...prev, type: e.target.value }))}
                            >
                                {TRANSFER_TYPES.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            {transferPanel.error && <p className="dpp-error">{transferPanel.error}</p>}
                            <button className="dpp-button" onClick={generateLink} disabled={transferPanel.busy}>
                                {transferPanel.busy ? 'Generating…' : 'Generate transfer link'}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="dpp-transfer-label">Share this link with the new owner:</p>
                            <div className="dpp-transfer-link-row">
                                <input className="dpp-input" value={transferPanel.link} readOnly />
                                <button
                                    className="dpp-button"
                                    onClick={() => navigator.clipboard.writeText(transferPanel.link)}
                                >
                                    Copy
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="dpp-page">
            <div className="dpp-header">
                <h1>Brand Dashboard</h1>
                <div className="dpp-header-actions">
                    <button className="dpp-button" onClick={() => navigate('/brand/products/new')}>+ New product</button>
                    <button className="dpp-link-button" onClick={logout}>Log out</button>
                </div>
            </div>

            {error && <p className="dpp-error">{error}</p>}

            <div className="dpp-brand-profile-card dpp-card">
                <div className="dpp-brand-profile-row">
                    {brand?.logo_url
                        ? <img className="dpp-brand-logo-preview" src={brand.logo_url} alt="Brand logo" />
                        : <>
                            <div className="dpp-brand-logo-placeholder">No logo</div>
                            <label className="dpp-link-button" style={{ cursor: 'pointer' }}>
                                {logoUploading ? 'Uploading…' : 'Upload logo'}
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadLogo} disabled={logoUploading} />
                            </label>
                          </>
                    }
                </div>
                <div className="dpp-brand-title-row">
                    <span className="dpp-muted">Passport display name</span>
                    {editTitle !== null ? (
                        <>
                            <input
                                className="dpp-input dpp-brand-title-input"
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                placeholder={brand?.name}
                            />
                            <button className="dpp-button" onClick={saveTitle} disabled={savingTitle}>
                                {savingTitle ? 'Saving…' : 'Save'}
                            </button>
                            <button className="dpp-link-button" onClick={() => setEditTitle(null)}>Cancel</button>
                        </>
                    ) : (
                        <>
                            <strong>{brand?.brand_title || brand?.name}</strong>
                            <button className="dpp-link-button" onClick={() => setEditTitle(brand?.brand_title || '')}>
                                Edit
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="dpp-card dpp-products-card" style={{ marginTop: 24 }}>
                <h2>Your products ({unsold.length})</h2>
                {unsold.length === 0 && <p className="dpp-muted">No unsold products.</p>}
                {unsold.map(p => renderProductRow(p, true))}
            </div>

            {sold.length > 0 && (
                <div className="dpp-card dpp-products-card" style={{ marginTop: 24 }}>
                    <h2>Sold products ({sold.length})</h2>
                    {sold.map(p => renderProductRow(p, false))}
                </div>
            )}
        </div>
    );
};

export default BrandDashboard;
